import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ProgressItem } from './ProgressItem';
import ProjectCard from '../project/projectItem/ProjectItem';
import ProjectItemEmpty from '../project/ProjectItemEmpty';
import { ProjectModel, ProjectOverview, TeamMember } from '../../store/types';
import { getProjectOverview } from '../../store/actions/index';
import { InvitationStatus, ProjectStatus } from '../../utils/utils';
import { getVisibleProjectSettings, RootState } from '../../store';
import {
    userDashboardProgressCard1,
    userDashboardProgressCard2,
    userDashboardProgressCard3,
} from '../../img/images';
import UserTutorialModal from './tutorial/UserTutorialModal';
import SusyButton from '../shared/buttons/SusyButton';

import { SusyTabs } from '../shared/tabs/SusyTabs';
import ProfileWidget from '../supervision/dashboard/ProfileWidget';
import useWindowDimensions, {
    BootstrapBreakpoints,
} from '../shared/hooks/UseWindowDimensions';

import './../profile/ProfileSidebar.css';
import './ThesisOverview.css';

const UserDashboard = () => {
    const [currentActiveButton, setCurrentActiveButton] = useState<string>(
        UserDashboardTabNames.yourProjects
    );

    const currentUser = useSelector(
        (state: RootState) => state.auth.currentUser
    );
    const projectOverview: ProjectOverview = useSelector(
        (state: RootState) => state.projectOverview
    );

    const localization = useSelector(
        (state: RootState) =>
            state.localization.localizationStrings.susy.thesisManagement
                .userDashboard
    );

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (currentUser?._id) {
            dispatch(getProjectOverview(currentUser._id));
        }
        dispatch(getVisibleProjectSettings());
    }, [currentUser?._id]);

    const { windowWidth } = useWindowDimensions();

    const isPendingInvitation = (member: TeamMember) => {
        return member.invitationStatus === InvitationStatus.pending;
    };

    const getPendingInvitations = () => {
        const allInvitations = projectOverview?.invites ?? [];
        return allInvitations.filter((project) => {
            const pending = project.teamList?.filter((member: TeamMember) => {
                return (
                    member?.member?._id === currentUser?._id &&
                    isPendingInvitation(member)
                );
            });
            return pending.length && project?.dates?.proposed;
        });
    };

    const getMyProjects = () => {
        const allInvitations = projectOverview?.invites ?? [];
        const acceptedInvitations = allInvitations.filter((project) => {
            const pending = project.teamList?.filter((member: TeamMember) => {
                return (
                    member?.member?._id === currentUser?._id &&
                    member.invitationStatus === InvitationStatus.accepted
                );
            });
            return pending.length;
        });
        return [...(projectOverview?.projects ?? []), ...acceptedInvitations];
    };

    const getApprovedProjectsNum = () => {
        const myProjects = getMyProjects();
        const approvedProjects = myProjects.filter((project) => {
            return project?.dates?.approved;
        });
        return approvedProjects.length;
    };

    const calculateProgress = () => {
        const myProjects = getMyProjects();
        let progressSum = 0;
        myProjects.forEach((project) => {
            // status 'saved'/'declined' : 20%
            let pg = 20;
            if (project?.dates?.proposed && !project?.dates?.declined) {
                // status 'pending' : 40%
                pg = 40;
            }
            if (project?.dates?.approved) {
                // status 'accepted' : 60%
                pg = 60;
            }
            if (project?.dates?.registered) {
                // status 'registered' : 80%
                pg = 80;
            }
            if (project?.dates?.handedIn) {
                // status 'hand in' : 100%
                pg = 100;
            }
            progressSum += pg;
        });

        return myProjects.length
            ? (progressSum / myProjects.length).toFixed(1)
            : 0;
    };

    const areActionsDisabled = () => {
        return !!currentUser?.waitingDelete;
    };

    const renderYourProjects = () => (
        <div
            className={`row ${
                currentActiveButton === UserDashboardTabNames.yourProjects
                    ? ''
                    : 'hide'
            }`}
        >
            {projectOverview &&
                projectOverview.projects &&
                getMyProjects()?.map((project, index) => (
                    <React.Fragment key={project._id ? project._id : index}>
                        <ProjectCard
                            linkTo={getProjextCarNavigationLink(project)}
                            project={project}
                        />
                    </React.Fragment>
                ))}

            {!areActionsDisabled() && (
                <ProjectItemEmpty
                    onClick={() => navigate('/user/selectProjectType')}
                />
            )}
        </div>
    );

    const getProjextCarNavigationLink = (project: ProjectModel) => {
        if (project?.currentStatus === ProjectStatus.saved) {
            return `/user/projectUpdate/${project._id}`;
        }

        if (
            project.currentStatus === ProjectStatus.pending &&
            !project.supervisor?._id
        ) {
            return `/user/selectSupervisor/${project._id}/selectSupervisor`;
        }

        return `/user/projectDetails/${project._id}`;
    };

    const renderInvitations = () => (
        <div
            className={`row ${
                currentActiveButton === UserDashboardTabNames.invitations
                    ? ''
                    : 'hide'
            }`}
        >
            {projectOverview &&
                projectOverview.invites &&
                getPendingInvitations()?.map((project, index) => (
                    <React.Fragment key={project._id ? project._id : index}>
                        <ProjectCard
                            linkTo={`/user/projectDetails/${project._id}`}
                            project={project}
                        />
                    </React.Fragment>
                ))}
        </div>
    );

    const userDashboardNavigation = (currentTab: string) => {
        navigate(`/user/dashboard/${currentTab}`);
        setCurrentActiveButton(currentTab);
    };

    const selectProjectTypeButton = () => (
        <SusyButton
            text={localization.AddNew}
            onClickHandler={() => {
                navigate('/user/selectProjectType');
            }}
            classes={`${
                currentActiveButton === UserDashboardTabNames.yourProjects
                    ? ''
                    : 'hide'
            }`}
            htmlAttributes={{
                disabled: areActionsDisabled(),
            }}
            size='medium'
        />
    );

    return (
        <div>
            <div className='dashboard-title-container col-sm-12 d-flex'>
                <div className='flex-fill'>
                    <p className='dashboard-title'>
                        {localization.UserDashboard}
                    </p>
                    <p className='dashboard-welcome'>
                        {localization.Welcome},{' '}
                        {currentUser?.firstName + ' ' + currentUser?.lastName}
                    </p>
                </div>
                {currentUser && windowWidth >= BootstrapBreakpoints.md && (
                    <ProfileWidget currentUser={currentUser} />
                )}
            </div>
            <div className='row'>
                <div className='col-12'>
                    {/* Project Progress Overview */}
                    <div className='row'>
                        <ProgressItem
                            imgLink={userDashboardProgressCard1}
                            name={localization.PendingInvitations}
                            value={(
                                getPendingInvitations()?.length ?? 0
                            )?.toString()}
                        />

                        <ProgressItem
                            imgLink={userDashboardProgressCard2}
                            name={localization.ApprovedProjects}
                            value={getApprovedProjectsNum()?.toString()}
                        />

                        <ProgressItem
                            imgLink={userDashboardProgressCard3}
                            name={localization.ProjectsInProgress}
                            value={calculateProgress()?.toString() + '%'}
                        />
                    </div>

                    {/* Projects/Invites Switch */}
                    <div className='susy-mt-2'>
                        <SusyTabs
                            rootNavigation='/user/dashboard'
                            tabs={[
                                {
                                    tabHeader: localization.YourProjects,
                                    tabName: UserDashboardTabNames.yourProjects,
                                    content: renderYourProjects(),
                                    customClickNavigation:
                                        userDashboardNavigation,
                                    isTabActive:
                                        currentActiveButton ===
                                        UserDashboardTabNames.yourProjects,
                                    renderHeaderRightInsert:
                                        selectProjectTypeButton(),
                                },
                                {
                                    tabHeader: localization.Invitations,
                                    tabName: UserDashboardTabNames.invitations,
                                    content: renderInvitations(),
                                    customClickNavigation:
                                        userDashboardNavigation,
                                    isTabActive:
                                        currentActiveButton ===
                                        UserDashboardTabNames.invitations,
                                },
                            ]}
                        />
                    </div>
                </div>
            </div>
            <UserTutorialModal
                initialOpen={currentUser?.firstTimeLogin || false}
            />
        </div>
    );
};

export enum UserDashboardTabNames {
    yourProjects = 'yourProjects',
    invitations = 'invitations',
}

export default UserDashboard;
